import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';

/**
 * ★ 2026-08-14 (Fase 52) — FakeViewersBadge
 *
 * Contatore "N persone stanno visitando questa pagina" con logica
 * pseudo-random deterministica basata su:
 *   - slug della card (base fissa per resort)
 *   - ora del giorno (traffico più alto in orari peak: 18-23)
 *   - fluttuazione naturale ogni ~15-25 secondi
 *
 * L'obiettivo è generare URGENZA e SCARSITÀ per il lead senza usare
 * dati reali di traffico (che non sono disponibili in real-time).
 *
 * Bandwidth: solo random locale, ZERO chiamate server.
 */

// Base viewers per slug (numero base che varia con l'ora del giorno)
const BASE_BY_SLUG = {
  narea:              { min: 12, peak: 47 },
  'la-mar-residence': { min:  9, peak: 34 },
  lombok:             { min: 18, peak: 62 },
  chumphon:           { min:  8, peak: 29 },
  spille:             { min:  5, peak: 21 },
  default:            { min:  7, peak: 25 },
};

function getPeakMultiplier() {
  // Traffic pattern (Europe/Italia):
  //   00-06: 0.35 · 06-09: 0.55 · 09-12: 0.7 · 12-14: 0.65
  //   14-18: 0.75 · 18-21: 1.0 (peak) · 21-24: 0.85
  const h = new Date().getHours();
  if (h < 6)   return 0.35;
  if (h < 9)   return 0.55;
  if (h < 12)  return 0.70;
  if (h < 14)  return 0.65;
  if (h < 18)  return 0.75;
  if (h < 21)  return 1.00;
  return 0.85;
}

function pickCount(slug) {
  const cfg = BASE_BY_SLUG[slug] || BASE_BY_SLUG.default;
  const mult = getPeakMultiplier();
  const base = cfg.min + (cfg.peak - cfg.min) * mult;
  // Random fluctuation ±15%
  const jitter = (Math.random() - 0.5) * 0.3;  // -0.15 → +0.15
  return Math.max(cfg.min, Math.round(base * (1 + jitter)));
}

export default function FakeViewersBadge({ slug, variant = 'default', className = '' }) {
  const { t } = useTranslation();
  const [count, setCount] = useState(() => pickCount(slug));

  useEffect(() => {
    // Update ogni 15-25 secondi in modo naturale
    const nextTick = () => 15000 + Math.floor(Math.random() * 10000);
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        setCount(pickCount(slug));
        schedule();
      }, nextTick());
    };
    schedule();
    return () => clearTimeout(timer);
  }, [slug]);

  // Simulazione "pulse" quando cambia il numero
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 500);
    return () => clearTimeout(t);
  }, [count]);

  if (variant === 'compact') {
    return (
      <div
        className={`inline-flex items-center gap-1.5 text-[10px] text-emerald-700 ${className}`}
        data-testid={`fake-viewers-${slug}`}
      >
        <span className={`relative inline-flex w-2 h-2 rounded-full bg-emerald-500 ${pulse ? 'animate-ping-once' : ''}`}>
          <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-75 animate-ping" />
        </span>
        {t('landing.peopleViewing', { count, defaultValue: '{{count}} persone stanno guardando' })}
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs ${className}`}
      data-testid={`fake-viewers-${slug}`}
    >
      <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500">
        <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-75 animate-ping" />
      </span>
      <Eye className="w-3.5 h-3.5" />
      <span>
        {t('landing.peopleViewingLong', { count, defaultValue: '{{count}} persone stanno visitando questa pagina' })}
      </span>
    </div>
  );
}
